/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

export default
class MercatorProjection {
    private DegRad: number;
    private RMax: number;
    private RMin: number;
    private LatMin: number;
    private LatMax: number;
    private Eccent: number;

    constructor() {
        this.DegRad = Math.PI / 180;
        this.RMax = 6378137;
        this.RMin = 6356752.3142;
        this.LatMin = -89.5;
        this.LatMax = 89.5;
        this.Eccent = Math.sqrt(1 - ((this.RMin / this.RMax) * (this.RMin / this.RMax)));
    }


    private _rad(aAngleDeg: number): number {
        return aAngleDeg * this.DegRad;
    }

    private _deg(aAngleRad: number): number {
        return aAngleRad * (180.0 / Math.PI);
    }

    private _mercLng(aLongitude: number): number {
        return this.RMax * this._rad(aLongitude);
    }

    private _mercLat(aLatitude: number): number {
        if (aLatitude > this.LatMax) {
            aLatitude = this.LatMax;
        }
        if (aLatitude < this.LatMin) {
            aLatitude = this.LatMin;
        }

        let phi = this._rad(aLatitude);
        let con = this.Eccent * Math.sin(phi);
        let com = 0.5 * this.Eccent;
        let ts;

        con = Math.pow((1 - con) / (1 + con), com);
        ts = Math.tan(0.5 * (Math.PI * 0.5 - phi)) / con;
        return -(this.RMax * Math.log(ts));
    }

    public fromLngLatToPoint(aLongitude: number, aLatitude: number): { x: number, y: number } {
        return {
            x: this._mercLng(aLongitude),
            y: this._mercLat(aLatitude)
        };
    }

    private _pjPHI2(ts: number, e: number): number {
        let N_ITER = 15;
        let HALFPI = Math.PI / 2;

        let TOL = 0.0000000001;
        let Phi, con, dphi;
        let i;
        let eccnth = 0.5 * e;
        Phi = HALFPI - 2.0 * Math.atan(ts);
        i = N_ITER;
        do {
            con = e * Math.sin(Phi);
            dphi = HALFPI - 2.0 * Math.atan(ts * Math.pow((1.0 - con) / (1.0 + con), eccnth)) - Phi;
            Phi += dphi;
        } while (Math.abs(dphi) > TOL && --i);

        return Phi;
    }

    public fromPointToLngLat(aPointX: number, aPointY: number): { latitude: number, longitude: number } {
        let lon = this._deg((aPointX / this.RMax));

        let temp = this.RMin / this.RMax;
        let e = Math.sqrt(1.0 - (temp * temp));
        let lat = this._deg(this._pjPHI2(Math.exp(0 - (aPointY / this.RMax)), e));

        return {'latitude': lat, 'longitude': lon};
    }
}

